import React, { useState, useMemo } from 'react';
import { 
  User as UserIcon, Home, Megaphone, ClipboardList, Truck, 
  FileCheck, BarChart3, ArrowLeft, Plus 
} from 'lucide-react';
import { 
  AVAILABLE_ROLES, initialMaterialsData 
} from '../data/mockData';
import MultiSelectBase from '../components/MultiSelectBase';

const MobileHeader = ({ title, user, onRoleChange }) => {
  const [isRoleMenuOpen, setIsRoleMenuOpen] = useState(false);
  return (
    <div className="fixed top-0 left-0 w-full bg-slate-900 text-white p-4 z-50 shadow-md">
      <div className="flex justify-between items-center">
        <h1 className="text-lg font-bold">{title}</h1>
        <div className="relative">
             <button onClick={() => setIsRoleMenuOpen(!isRoleMenuOpen)} className="flex items-center gap-2 text-xs bg-slate-800 px-3 py-1.5 rounded-full border border-slate-700">
                <UserIcon size={14}/> {AVAILABLE_ROLES.find(r => r.id === user)?.label.slice(0,4)}
             </button>
             {isRoleMenuOpen && (
                 <div className="absolute right-0 top-full mt-2 w-40 bg-white rounded-lg shadow-xl py-1 text-slate-800 z-50 border border-slate-100">
                     {AVAILABLE_ROLES.map(r => (
                         <button key={r.id} onClick={() => { onRoleChange(r.id); setIsRoleMenuOpen(false); }} className="block w-full text-left px-4 py-2 text-xs hover:bg-blue-50">
                            {r.label}
                         </button>
                     ))}
                 </div>
             )}
        </div>
      </div>
    </div>
  );
};

const MobileTabBar = ({ activeTab, setActiveTab }) => {
  const tabs = [
    { id: 'dashboard', label: '首页', icon: Home },
    { id: 'demand', label: '需求', icon: Megaphone },
    { id: 'approval', label: '审批', icon: ClipboardList },
    { id: 'reservation', label: '预约', icon: Truck },
  ];
  return (
    <div className="fixed bottom-0 left-0 w-full bg-white border-t border-slate-200 flex justify-around py-2 z-50">
      {tabs.map((tab) => {
        const isActive = activeTab === tab.id;
        return (
          <button key={tab.id} onClick={() => setActiveTab(tab.id)} className={`flex flex-col items-center gap-1 ${isActive ? 'text-blue-600' : 'text-slate-400'}`}>
            <tab.icon size={20} className={isActive ? 'stroke-current' : ''} />
            <span className="text-[10px] font-medium">{tab.label}</span>
          </button>
        );
      })}
    </div>
  );
};

const MobileHomeView = ({ setActiveTab, stockIns = [], approvals = [] }) => {
    const incomingWeight = useMemo(() => {
        // Calculate total weight (simplified: sum of all stockIns for now, or filter by today if needed)
        // For demo, sum all.
        return stockIns.reduce((sum, s) => sum + (parseFloat(s.weight) || 0), 0).toFixed(1);
    }, [stockIns]);

    const pendingApprovalsCount = useMemo(() => {
        return approvals.filter(t => t.status === 'pending').length;
    }, [approvals]);

    return (
    <div className="p-4 space-y-6 pb-20 pt-20">
        <div className="grid grid-cols-2 gap-4">
             <div className="bg-blue-600 text-white p-4 rounded-2xl shadow-lg shadow-blue-200">
                 <div className="text-xs opacity-80 mb-1">今日收货 (吨)</div>
                 <div className="text-2xl font-bold">{incomingWeight}</div>
             </div>
             <div className="bg-white p-4 rounded-2xl shadow-sm border border-slate-100">
                 <div className="text-xs text-slate-400 mb-1">待办审批</div>
                 <div className="text-2xl font-bold text-orange-500">{pendingApprovalsCount}</div>
             </div>
        </div>
        <div>
            <h3 className="font-bold text-slate-800 mb-4 text-sm">常用功能</h3>
            <div className="grid grid-cols-4 gap-4">
                {[
                    { label: '发需求', icon: Megaphone, color: 'text-blue-500', bg: 'bg-blue-50', tab: 'demand' },
                    { label: '审单据', icon: FileCheck, color: 'text-orange-500', bg: 'bg-orange-50', tab: 'approval' },
                    { label: '查预约', icon: Truck, color: 'text-green-500', bg: 'bg-green-50', tab: 'reservation' },
                    { label: '看报表', icon: BarChart3, color: 'text-purple-500', bg: 'bg-purple-50', tab: 'dashboard' },
                ].map((action, i) => (
                    <button key={i} onClick={() => setActiveTab(action.tab)} className="flex flex-col items-center gap-2">
                        <div className={`w-12 h-12 rounded-2xl ${action.bg} flex items-center justify-center ${action.color}`}>
                            <action.icon size={20}/>
                        </div>
                        <span className="text-xs text-slate-600">{action.label}</span>
                    </button>
                ))}
            </div>
        </div>
        <div className="bg-white rounded-xl p-4 shadow-sm border border-slate-100">
            <h3 className="font-bold text-slate-800 mb-3 text-sm">最新动态</h3>
            <div className="space-y-3">
                <div className="flex gap-3 text-xs">
                    <div className="w-2 h-2 rounded-full bg-blue-500 mt-1.5 shrink-0"></div>
                    <div className="text-slate-600">
                        <span className="font-bold text-slate-800">王五</span> 刚刚预约了 50吨 工业重废
                        <div className="text-slate-400 mt-1">10分钟前</div>
                    </div>
                </div>
                 <div className="flex gap-3 text-xs">
                    <div className="w-2 h-2 rounded-full bg-green-500 mt-1.5 shrink-0"></div>
                    <div className="text-slate-600">
                        <span className="font-bold text-slate-800">李部长</span> 批准了 新增基地申请
                        <div className="text-slate-400 mt-1">30分钟前</div>
                    </div>
                </div>
            </div>
        </div>
    </div>
)};

const MobileCreateDemand = ({ onBack }) => {
    const DEFAULT_FORM_STATE = {
        demandType: 'open',
        selectedBases: [],
        demandItems: [{ id: 1, material: '', price: '', qty: '', qtyUnlimited: false, deliveryFrom: '', deliveryTo: '' }],
        validFrom: '',
        validTo: '',
        validToUnlimited: true, 
        deliveryValidMonths: 6,
        targetSuppliers: [],
    };

    const [formData, setFormData] = useState(DEFAULT_FORM_STATE);
    const { demandType, selectedBases, demandItems, validFrom, validTo, validToUnlimited, deliveryValidMonths, targetSuppliers } = formData;
    const [supplierInput, setSupplierInput] = useState('');

    const updateFormData = (field, value) => { setFormData(prev => ({ ...prev, [field]: value })); };
    const addDemandItem = () => { updateFormData('demandItems', [...demandItems, { id: Date.now(), material: '', price: '', qty: '', qtyUnlimited: false, deliveryFrom: '', deliveryTo: '' }]); };
    const removeDemandItem = (id) => { if (demandItems.length > 1) updateFormData('demandItems', demandItems.filter(item => item.id !== id)); };
    const updateDemandItem = (id, field, value) => { updateFormData('demandItems', demandItems.map(item => item.id === id ? { ...item, [field]: value } : item)); };

    const availableMaterials = useMemo(() => {
        return initialMaterialsData.filter(m => selectedBases.length > 0 && m.baseIds?.some(bid => selectedBases.includes(bid)));
    }, [selectedBases]);

    const handleSubmit = () => {
        if(selectedBases.length === 0) { alert('请选择至少一个收货基地'); return; }
        const validItems = demandItems.filter(item => item.material && item.price && (item.qty || item.qtyUnlimited));
        if(validItems.length === 0) { alert('请至少填写一条完整的物料明细'); return; }
        alert('需求发布成功');
        onBack();
    };

    return (
        <div className="p-4 pb-20 pt-20">
             <button onClick={onBack} className="flex items-center gap-1 text-sm text-slate-500 mb-4"><ArrowLeft size={16}/> 返回</button>
             <h3 className="font-bold text-lg mb-6">新建采购需求</h3>
             <div className="space-y-4">
                 <div className="bg-white p-4 rounded-xl shadow-sm border border-slate-100 space-y-4">
                     <div>
                         <label className="block text-xs font-bold text-slate-500 mb-2">需求类型</label>
                         <div className="flex gap-2">
                             <button onClick={() => updateFormData('demandType', 'open')} className={`flex-1 py-2 rounded-lg text-sm font-medium border ${demandType === 'open' ? 'bg-blue-50 text-blue-600 border-blue-100' : 'bg-white text-slate-500 border-slate-200'}`}>敞口需求</button>
                             <button onClick={() => updateFormData('demandType', 'closed')} className={`flex-1 py-2 rounded-lg text-sm font-medium border ${demandType === 'closed' ? 'bg-purple-50 text-purple-600 border-purple-100' : 'bg-white text-slate-500 border-slate-200'}`}>闭口需求</button>
                         </div>
                     </div>
                     <div>
                         <label className="block text-xs font-bold text-slate-500 mb-2">选择收货基地 <span className="text-red-500">*</span></label>
                         <MultiSelectBase selectedIds={selectedBases} onChange={(ids) => updateFormData('selectedBases', ids)} placeholder="请选择 (可多选)..." />
                         {selectedBases.length === 0 && <p className="text-[10px] text-orange-500 mt-1">请先选择基地以加载物料</p>}
                     </div>
                 </div>
                 
                 <div className="space-y-4">
                     <div className="flex justify-between items-center px-1">
                        <label className="text-sm font-bold text-slate-700">物料明细</label>
                        <button onClick={addDemandItem} className="text-xs text-blue-600 flex items-center gap-1"><Plus size={14}/> 添加物料</button>
                     </div>
                     {demandItems.map((item) => (
                        <div key={item.id} className="bg-white p-4 rounded-xl shadow-sm border border-slate-100 relative">
                            {demandItems.length > 1 && <button onClick={() => removeDemandItem(item.id)} className="absolute top-2 right-2 text-red-400 hover:text-red-500 text-xs">删除</button>}
                            <div className="space-y-3">
                                <div>
                                    <label className="block text-[10px] text-slate-400 mb-1">选择物料 {selectedBases.length ? '' : '(请先选择基地)'}</label>
                                    <select className="w-full p-2 bg-slate-50 border border-slate-200 rounded-lg text-sm outline-none" value={item.material} onChange={(e) => updateDemandItem(item.id, 'material', e.target.value)} disabled={!selectedBases.length}>
                                        <option value="">请选择物料...</option>
                                        {availableMaterials.map(m => <option key={m.id} value={m.name}>{m.name}</option>)}
                                    </select>
                                </div>
                                <div className="grid grid-cols-2 gap-3">
                                    <div>
                                        <label className="block text-[10px] text-slate-400 mb-1">{demandType === 'open' ? '单价 (元/吨)' : '固定价(元/吨)'}</label>
                                        <input type="number" className="w-full p-2 border border-slate-200 rounded-lg text-sm outline-none" value={item.price} onChange={(e) => updateDemandItem(item.id, 'price', e.target.value)} placeholder="输入单价" />
                                    </div>
                                    <div>
                                        <label className="block text-[10px] text-slate-400 mb-1">数量 (吨)</label>
                                        <div className="flex items-center gap-2">
                                            <input type="number" className="w-full p-2 border border-slate-200 rounded-lg text-sm outline-none disabled:bg-slate-100" value={item.qty} onChange={(e) => updateDemandItem(item.id, 'qty', e.target.value)} placeholder={item.qtyUnlimited ? '不限' : '输入数量'} disabled={item.qtyUnlimited} />
                                            {demandType === 'open' && (
                                                <label className="flex items-center gap-1 text-[10px] text-slate-500 whitespace-nowrap">
                                                    <input type="checkbox" checked={item.qtyUnlimited} onChange={(e) => { const isChecked = e.target.checked; updateFormData('demandItems', demandItems.map(i => i.id === item.id ? { ...i, qtyUnlimited: isChecked, qty: isChecked ? '' : i.qty } : i)); }}/>不限
                                                </label>
                                            )}
                                        </div>
                                    </div>
                                </div>
                                {demandType === 'closed' && (
                                    <div className="grid grid-cols-2 gap-3 pt-2 border-t border-slate-50">
                                        <div>
                                            <label className="block text-[10px] text-slate-400 mb-1">收货有效(起)</label>
                                            <input type="date" className="w-full p-1.5 border border-slate-200 rounded-lg text-xs" value={item.deliveryFrom} onChange={e => updateDemandItem(item.id, 'deliveryFrom', e.target.value)} />
                                        </div>
                                        <div>
                                            <label className="block text-[10px] text-slate-400 mb-1">收货有效(止)</label>
                                            <input type="date" className="w-full p-1.5 border border-slate-200 rounded-lg text-xs" value={item.deliveryTo} onChange={e => updateDemandItem(item.id, 'deliveryTo', e.target.value)} />
                                        </div>
                                    </div>
                                )}
                            </div>
                        </div>
                     ))}
                 </div>

                 {demandType === 'open' && (
                     <div className="bg-white p-4 rounded-xl shadow-sm border border-slate-100 space-y-4">
                        <label className="block text-sm font-bold text-slate-700">有效期设置</label>
                        <div className="grid grid-cols-2 gap-3">
                            <div>
                                <label className="block text-[10px] text-slate-400 mb-1">价格有效(起)</label>
                                <input type="date" className="w-full p-2 border border-slate-200 rounded-lg text-sm" value={validFrom} onChange={e => updateFormData('validFrom', e.target.value)} />
                            </div>
                            <div>
                                <label className="block text-[10px] text-slate-400 mb-1">价格有效(止)</label>
                                <input type="date" disabled={validToUnlimited} className="w-full p-2 border border-slate-200 rounded-lg text-sm disabled:text-transparent" value={validTo} onChange={e => updateFormData('validTo', e.target.value)} />
                                <label className="flex items-center gap-1 text-[10px] text-slate-500 mt-1">
                                    <input type="checkbox" checked={validToUnlimited} onChange={(e) => { setFormData(prev => ({...prev, validToUnlimited: e.target.checked, validTo: e.target.checked ? '' : prev.validTo})); }}/> 长期有效
                                </label>
                            </div>
                        </div>
                        <div>
                            <label className="block text-[10px] text-slate-400 mb-1">收货有效期 (月)</label>
                            <div className="flex items-center gap-2">
                                <button className="px-3 py-1 bg-slate-100 rounded" onClick={() => updateFormData('deliveryValidMonths', Math.max(1, deliveryValidMonths - 1))}>-</button>
                                <span className="w-8 text-center text-sm">{deliveryValidMonths}</span>
                                <button className="px-3 py-1 bg-slate-100 rounded" onClick={() => updateFormData('deliveryValidMonths', Math.min(24, deliveryValidMonths + 1))}>+</button>
                            </div>
                        </div>
                     </div>
                 )}

                 {demandType === 'closed' && (
                     <div className="bg-purple-50 p-4 rounded-xl shadow-sm border border-purple-100 space-y-4">
                        <label className="block text-sm font-bold text-purple-700">闭口设置</label>
                        <div className="grid grid-cols-2 gap-3">
                            <div>
                                <label className="block text-[10px] text-slate-400 mb-1">价格有效(起)</label>
                                <input type="date" className="w-full p-2 border border-purple-200 rounded-lg text-sm" value={validFrom} onChange={e => updateFormData('validFrom', e.target.value)} />
                            </div>
                            <div>
                                <label className="block text-[10px] text-slate-400 mb-1">价格有效(止)</label>
                                <input type="date" className="w-full p-2 border border-purple-200 rounded-lg text-sm" value={validTo} onChange={e => updateFormData('validTo', e.target.value)} />
                            </div>
                        </div>
                        <div>
                            <label className="block text-[10px] text-purple-400 mb-1">定向供应商</label>
                            <div className="flex gap-2">
                                <input type="text" className="flex-1 p-2 border border-purple-200 rounded-lg text-sm" placeholder="输入名称" value={supplierInput} onChange={e => setSupplierInput(e.target.value)} />
                                <button className="bg-purple-600 text-white px-3 rounded-lg text-sm" onClick={() => { if(supplierInput) { updateFormData('targetSuppliers', [...targetSuppliers, supplierInput]); setSupplierInput(''); } }}>添加</button>
                            </div>
                            <div className="flex flex-wrap gap-2 mt-2">
                                {targetSuppliers.map((s, i) => (
                                    <span key={i} className="text-xs bg-white border border-purple-200 text-purple-600 px-2 py-1 rounded flex items-center gap-1">
                                        {s} <button onClick={() => updateFormData('targetSuppliers', targetSuppliers.filter((_, idx) => idx !== i))}>×</button>
                                    </span>
                                ))}
                            </div>
                        </div>
                     </div>
                 )}
                 <button className="w-full py-3 bg-blue-600 text-white rounded-xl font-bold shadow-lg shadow-blue-200 active:scale-95 transition-transform" onClick={handleSubmit}>立即发布</button>
             </div>
        </div>
    );
};

const MobileCreateReservation = ({ onBack, orders = [] }) => (
     <div className="p-4 pb-20 pt-20">
         <button onClick={onBack} className="flex items-center gap-1 text-sm text-slate-500 mb-4"><ArrowLeft size={16}/> 返回</button>
         <h3 className="font-bold text-lg mb-6">新建发货预约</h3>
         <div className="space-y-4">
             <div className="bg-white p-4 rounded-xl shadow-sm border border-slate-100 space-y-4">
                  <div>
                     <label className="block text-xs font-bold text-slate-500 mb-2">关联采购订单</label>
                     <select className="w-full p-2 bg-white border border-slate-200 rounded-lg text-sm outline-none">
                         <option>请选择订单...</option>
                         {orders.filter(o => o.status !== '已完成').map(o => <option key={o.id} value={o.id}>{o.id} - {o.supplier}</option>)}
                     </select>
                 </div>
             </div>
             <div className="bg-white p-4 rounded-xl shadow-sm border border-slate-100 space-y-4">
                 <div>
                     <label className="block text-xs font-bold text-slate-500 mb-2">车牌号码</label>
                     <input type="text" className="w-full p-2 border border-slate-200 rounded-lg text-sm outline-none" placeholder="请输入车牌号 (如: 浙J88888)" />
                 </div>
                  <div>
                     <label className="block text-xs font-bold text-slate-500 mb-2">预约日期</label>
                     <input type="date" className="w-full p-2 border border-slate-200 rounded-lg text-sm outline-none text-slate-600" />
                 </div>
                 <div className="grid grid-cols-2 gap-4">
                     <div>
                         <label className="block text-xs font-bold text-slate-500 mb-2">司机姓名</label>
                         <input type="text" className="w-full p-2 border border-slate-200 rounded-lg text-sm outline-none" placeholder="司机姓名" />
                     </div>
                     <div>
                         <label className="block text-xs font-bold text-slate-500 mb-2">联系电话</label>
                         <input type="tel" className="w-full p-2 border border-slate-200 rounded-lg text-sm outline-none" placeholder="手机号" />
                     </div>
                 </div>
             </div>

             <button className="w-full py-3 bg-blue-600 text-white rounded-xl font-bold shadow-lg shadow-blue-200 active:scale-95 transition-transform" onClick={() => { alert('预约提交成功'); onBack(); }}>提交预约</button>
         </div>
    </div>
);

const MobileDemandDetail = ({ demand, onBack }) => (
    <div className="p-4 pb-20 pt-20">
         <button onClick={onBack} className="flex items-center gap-1 text-sm text-slate-500 mb-4"><ArrowLeft size={16}/> 返回</button>
         <h3 className="font-bold text-lg mb-6">需求详情</h3>
         <div className="space-y-4">
             <div className="bg-white p-4 rounded-xl shadow-sm border border-slate-100">
                <div className="flex justify-between border-b border-slate-50 pb-2 mb-2">
                    <span className="font-bold text-slate-700">{demand.material}</span>
                    <span className="text-xs bg-blue-50 text-blue-600 px-2 py-0.5 rounded">{demand.type}</span>
                </div>
                <div className="grid grid-cols-2 gap-2 text-sm">
                    <div className="text-slate-500">编号: <span className="text-slate-800">{demand.id}</span></div>
                    <div className="text-slate-500">状态: <span className="text-green-600">{demand.status}</span></div>
                    <div className="text-slate-500">价格: <span className="text-red-600 font-bold">{demand.price}</span></div>
                    <div className="text-slate-500">数量: <span className="text-slate-800">{demand.qty}</span></div>
                    <div className="col-span-2 text-slate-500">有效期: <span className="text-slate-800">{demand.validUntil}</span></div>
                    <div className="col-span-2 text-slate-500">创建人: <span className="text-slate-800">{demand.creator}</span></div>
                </div>
             </div>
             <div className="flex gap-3">
                 <button className="flex-1 py-3 bg-white text-blue-600 border border-blue-600 rounded-xl font-bold active:scale-95 transition-transform" onClick={() => alert('复制成功')}>复制需求</button>
                 <button className="flex-1 py-3 bg-blue-600 text-white rounded-xl font-bold shadow-lg shadow-blue-200 active:scale-95 transition-transform" onClick={() => alert('编辑功能开发中')}>编辑</button>
             </div>
         </div>
    </div>
);

const MobileReservationDetail = ({ reservation, onBack }) => (
    <div className="p-4 pb-20 pt-20">
         <button onClick={onBack} className="flex items-center gap-1 text-sm text-slate-500 mb-4"><ArrowLeft size={16}/> 返回</button>
         <h3 className="font-bold text-lg mb-6">预约详情</h3>
         <div className="space-y-4">
             <div className="bg-white p-4 rounded-xl shadow-sm border border-slate-100">
                <div className="flex justify-between items-center mb-4">
                    <div className="flex items-center gap-2">
                        <Truck size={20} className="text-blue-500"/>
                        <span className="font-bold text-lg">{reservation.plate}</span>
                    </div>
                    <span className={`px-2 py-1 text-xs rounded ${reservation.status === '已入场' ? 'bg-green-100 text-green-700' : 'bg-blue-50 text-blue-600'}`}>{reservation.status}</span>
                </div>
                <div className="space-y-2 text-sm text-slate-600">
                    <div className="flex justify-between"><span>预约单号</span><span>{reservation.id}</span></div>
                    <div className="flex justify-between"><span>供应商</span><span>{reservation.supplier}</span></div>
                    <div className="flex justify-between"><span>物料</span><span>{reservation.material}</span></div>
                    <div className="flex justify-between"><span>预约时间</span><span>{reservation.time}</span></div>
                    <div className="flex justify-between"><span>司机</span><span>{reservation.driver}</span></div>
                    <div className="flex justify-between"><span>联系电话</span><span>{reservation.contact}</span></div>
                </div>
             </div>
             {reservation.status !== '已入场' && (
                 <button className="w-full py-3 bg-red-50 text-red-600 border border-red-100 rounded-xl font-bold active:scale-95 transition-transform" onClick={() => alert('取消预约成功')}>取消预约</button>
             )}
         </div>
    </div>
);

const MobileApprovalDetail = ({ task, onBack }) => (
    <div className="p-4 pb-20 pt-20">
         <button onClick={onBack} className="flex items-center gap-1 text-sm text-slate-500 mb-4"><ArrowLeft size={16}/> 返回</button>
         <h3 className="font-bold text-lg mb-6">审批详情</h3>
         <div className="space-y-4">
             <div className="bg-white p-4 rounded-xl shadow-sm border border-slate-100">
                 <h4 className="font-bold text-md mb-2">{task.title}</h4>
                 <div className="text-xs text-slate-500 mb-4 flex justify-between">
                     <span>申请人: {task.applicant}</span>
                     <span>{task.submitTime}</span>
                 </div>
                 <div className="bg-slate-50 p-3 rounded-lg space-y-2 text-sm">
                     {task.type === 'base_add' ? (
                        <>
                            <div className="flex justify-between"><span>基地名称</span><span className="font-medium">{task.details.name}</span></div>
                            <div className="flex justify-between"><span>负责人</span><span className="font-medium">{task.details.manager}</span></div>
                            <div><span>地址:</span> <span className="font-medium">{task.details.address}</span></div>
                        </>
                     ) : (
                        <>
                            <div className="flex justify-between"><span>物料</span><span className="font-medium">{task.details.material}</span></div>
                            <div className="flex justify-between"><span>价格</span><span className="font-medium">{task.details.price}</span></div>
                            <div className="flex justify-between"><span>数量</span><span className="font-medium">{task.details.qty}</span></div>
                        </>
                     )}
                 </div>
             </div>

             <div className="bg-white p-4 rounded-xl shadow-sm border border-slate-100">
                 <h4 className="font-bold text-sm mb-4">审批流程</h4>
                 <div className="space-y-4 relative pl-4 border-l-2 border-slate-100 ml-2">
                     {task.flow.map((node, i) => (
                         <div key={i} className="relative">
                             <div className={`absolute -left-[21px] top-1 w-3 h-3 rounded-full border-2 border-white ${node.status === 'completed' ? 'bg-green-500' : node.status === 'current' ? 'bg-blue-500' : 'bg-slate-300'}`}></div>
                             <div className="flex justify-between text-sm">
                                 <span className="font-medium text-slate-800">{node.step}</span>
                                 <span className="text-xs text-slate-400">{node.time}</span>
                             </div>
                             <div className="text-xs text-slate-500">{node.user}</div>
                         </div>
                     ))}
                 </div>
             </div>

             {task.status === 'pending' && (
                 <div className="fixed bottom-0 left-0 w-full bg-white border-t border-slate-200 p-4 flex gap-3 z-50">
                     <button className="flex-1 py-3 bg-red-50 text-red-600 rounded-xl font-bold active:scale-95 transition-transform" onClick={() => {alert('已驳回'); onBack();}}>驳回</button>
                     <button className="flex-1 py-3 bg-blue-600 text-white rounded-xl font-bold shadow-lg shadow-blue-200 active:scale-95 transition-transform" onClick={() => {alert('已通过'); onBack();}}>同意</button>
                 </div>
             )}
         </div>
    </div>
);

const MobileDemandView = ({ onNavigate, demands = [] }) => (
    <div className="p-4 space-y-4 pb-20 pt-20">
        {demands.map(d => (
            <div key={d.id} className="bg-white p-4 rounded-xl shadow-sm border border-slate-100" onClick={() => onNavigate('detail_demand', d)}>
                <div className="flex justify-between items-center mb-3">
                     <span className={`text-[10px] px-2 py-0.5 rounded border ${d.type === '敞口需求' ? 'bg-blue-50 text-blue-600 border-blue-100' : 'bg-purple-50 text-purple-600 border-purple-100'}`}>{d.type}</span>
                     <span className="text-xs text-slate-400">{d.status}</span>
                </div>
                <h3 className="font-bold text-slate-800 text-sm mb-2">{d.material}</h3>
                <div className="flex justify-between items-end">
                    <div>
                        <div className="text-lg font-bold text-red-600">¥ {d.price}</div>
                        <div className="text-[10px] text-slate-400">需量: {d.qty}</div>
                    </div>
                    <div className="text-right">
                         <p className="text-[10px] text-slate-400">有效期至</p>
                         <p className="text-xs text-slate-600 font-medium">{d.validUntil}</p>
                    </div>
                </div>
            </div>
        ))}
        <button className="fixed bottom-20 right-4 w-12 h-12 bg-blue-600 text-white rounded-full shadow-xl flex items-center justify-center active:scale-90 transition-transform" onClick={() => onNavigate('create_demand')}>
            <Plus size={24}/>
        </button>
    </div>
);

const MobileReservationView = ({ onNavigate, reservations = [] }) => (
    <div className="p-4 space-y-4 pb-20 pt-20">
        {reservations.map(res => (
            <div key={res.id} className="bg-white p-4 rounded-xl shadow-sm border border-slate-100 flex flex-col gap-3" onClick={() => onNavigate('detail_reservation', res)}>
                <div className="flex justify-between border-b border-slate-50 pb-2">
                    <span className="font-bold text-slate-800 flex items-center gap-2"><Truck size={16} className="text-blue-500"/> {res.plate}</span>
                    <span className={`text-xs px-2 py-0.5 rounded ${res.status === '已入场' ? 'bg-green-100 text-green-700' : 'bg-blue-50 text-blue-600'}`}>{res.status}</span>
                </div>
                <div className="grid grid-cols-2 gap-2 text-xs text-slate-500">
                    <div>物料: <span className="text-slate-800">{res.material}</span></div>
                    <div>司机: <span className="text-slate-800">{res.driver}</span></div>
                    <div className="col-span-2">预约时间: <span className="text-slate-800">{res.time}</span></div>
                </div>
                <div className="pt-2 flex justify-end">
                    <button className="text-xs bg-white border border-blue-600 text-blue-600 px-3 py-1.5 rounded-lg">查看详情</button>
                </div>
            </div>
        ))}
         <button className="fixed bottom-20 right-4 w-12 h-12 bg-blue-600 text-white rounded-full shadow-xl flex items-center justify-center active:scale-90 transition-transform" onClick={() => onNavigate('create_reservation')}>
            <Plus size={24}/>
        </button>
    </div>
);

const MobileApprovalView = ({ onNavigate, approvals = [] }) => (
    <div className="p-4 space-y-4 pb-20 pt-20">
         {approvals.map(task => (
             <div key={task.id} className="bg-white p-4 rounded-xl shadow-sm border border-slate-100 relative overflow-hidden" onClick={() => onNavigate('detail_approval', task)}>
                 <div className={`absolute left-0 top-0 bottom-0 w-1 ${task.status === 'pending' ? 'bg-orange-500' : 'bg-green-500'}`}></div>
                 <div className="flex justify-between items-start mb-2">
                     <div className="flex-1">
                         <div className="text-xs text-slate-400 mb-1">{task.id}</div>
                         <h3 className="font-bold text-slate-800 text-sm">{task.title}</h3>
                     </div>
                     <span className={`text-xs px-2 py-1 rounded ${task.status === 'pending' ? 'bg-orange-50 text-orange-600' : 'bg-green-50 text-green-600'}`}>
                         {task.status === 'pending' ? '待审批' : '已完成'}
                     </span>
                 </div>
                 <div className="flex justify-between items-end mt-4">
                     <div className="text-xs text-slate-500">
                         <p>申请人: {task.applicant}</p>
                         <p className="mt-1">时间: {task.submitTime}</p>
                     </div>
                     <button className="bg-blue-600 text-white text-xs px-4 py-2 rounded-lg shadow-md shadow-blue-200 active:scale-95 transition-transform">去处理</button>
                 </div>
             </div>
         ))}
    </div>
);

const MobileApp = ({ 
  currentRole, 
  onRoleChange, 
  forceMobilePreview, 
  onExitMobilePreview,
  orders = [],
  stockIns = [],
  reservations = [],
  demands = [],
  approvals = []
}) => {
  const [activeTab, setActiveTab] = useState('dashboard');
  const [viewMode, setViewMode] = useState('list'); 
  const [selectedItem, setSelectedItem] = useState(null);

  const handleNavigate = (mode, item = null) => {
      setViewMode(mode);
      if (item) setSelectedItem(item);
  };

  const renderContent = () => {
      if (viewMode === 'create_demand') return <MobileCreateDemand onBack={() => setViewMode('list')} />;
      if (viewMode === 'create_reservation') return <MobileCreateReservation onBack={() => setViewMode('list')} orders={orders} />;
      
      if (viewMode === 'detail_demand') return <MobileDemandDetail demand={selectedItem} onBack={() => setViewMode('list')} />;
      if (viewMode === 'detail_reservation') return <MobileReservationDetail reservation={selectedItem} onBack={() => setViewMode('list')} />;
      if (viewMode === 'detail_approval') return <MobileApprovalDetail task={selectedItem} onBack={() => setViewMode('list')} />;

      switch(activeTab) {
          case 'dashboard': return <MobileHomeView setActiveTab={setActiveTab} stockIns={stockIns} approvals={approvals} />;
          case 'approval': return <MobileApprovalView onNavigate={handleNavigate} approvals={approvals} />;
          case 'reservation': return <MobileReservationView onNavigate={handleNavigate} reservations={reservations} />;
          case 'demand': return <MobileDemandView onNavigate={handleNavigate} demands={demands} />;
          default: return <MobileHomeView setActiveTab={setActiveTab} stockIns={stockIns} approvals={approvals} />;
      }
  };

  const getTitle = () => {
      if (viewMode === 'create_demand') return '';
      if (viewMode === 'create_reservation') return '';
      if (viewMode.startsWith('detail_')) return '';

      switch(activeTab) {
          case 'dashboard': return '工作台';
          case 'approval': return '审批中心';
          case 'reservation': return '预约列表';
          case 'demand': return '采购需求';
          default: return '废钢直通车';
      }
  }

  return (
    <div className="bg-slate-50 min-h-screen font-sans text-slate-900 pb-20 relative">
      {forceMobilePreview && onExitMobilePreview && (
        <button
          onClick={onExitMobilePreview}
          className="fixed top-4 right-4 z-50 flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium text-slate-600 bg-white/95 backdrop-blur border border-slate-200 rounded-lg shadow-md hover:bg-slate-50 hover:border-slate-300 transition-colors"
          title="返回桌面端"
        >
          <ArrowLeft size={14} />
          返回桌面端
        </button>
      )}
      {viewMode === 'list' && <MobileHeader title={getTitle()} user={currentRole} onRoleChange={onRoleChange}/>}
      {renderContent()}
      {viewMode === 'list' && <MobileTabBar activeTab={activeTab} setActiveTab={setActiveTab} />}
    </div>
  );
};

export default MobileApp;